import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  api, createStory, deleteStory, getStory, listStories, publishStory as publishStoryApi,
  reorderStories, restoreStory as restoreStoryApi, unpublishStory, updateStory,
} from './api';
import type {
  ArticleDetail, ArticleListItem, AuditEntry, CategoryItem, LeadItem, MediaItem, Overview,
  PageDef, PageItem, Paginated, Readiness, RedirectItem, RevalidationResult, RoleItem,
  ScanEvent, ScanFilters, ScanMapData, ScanStats, ServiceDetail,
  ServiceListItem, StoryDetail, StoryInput, User,
} from './types';

/**
 * Query keys as data, so an invalidation can never miss a cache by typo.
 */
export const keys = {
  overview: ['overview'] as const,
  articles: (params?: Record<string, unknown>) => ['articles', params ?? {}] as const,
  article: (id: number | string) => ['article', String(id)] as const,
  readiness: (kind: string, id: number | string) => ['readiness', kind, String(id)] as const,
  services: () => ['services'] as const,
  service: (id: string) => ['service', id] as const,
  stories: (params?: Record<string, unknown>) => ['stories', params ?? {}] as const,
  story: (id: string) => ['story', id] as const,
  categories: ['categories'] as const,
  pages: ['pages'] as const,
  page: (route: string) => ['page', route] as const,
  media: (params?: Record<string, unknown>) => ['media', params ?? {}] as const,
  mediaUsage: (id: string) => ['mediaUsage', id] as const,
  redirects: (params?: Record<string, unknown>) => ['redirects', params ?? {}] as const,
  leads: (params?: Record<string, unknown>) => ['leads', params ?? {}] as const,
  users: ['users'] as const,
  roles: ['roles'] as const,
  audit: (params?: Record<string, unknown>) => ['audit', params ?? {}] as const,
  scans: (params?: Record<string, unknown>) => ['scans', params ?? {}] as const,
  scanStats: (params?: Record<string, unknown>) => ['scanStats', params ?? {}] as const,
  scanMap: (params?: Record<string, unknown>) => ['scanMap', params ?? {}] as const,
  scanOperators: ['scanOperators'] as const,
};

const get = async <T,>(url: string, params?: Record<string, unknown>): Promise<T> =>
  (await api.get<T>(url, { params })).data;

// ── Reads ───────────────────────────────────────────────────────────────

export const useOverview = () =>
  useQuery({ queryKey: keys.overview, queryFn: () => get<Overview>('/overview') });

export const useArticles = (params: Record<string, unknown>, enabled = true) =>
  useQuery({
    queryKey: keys.articles(params),
    queryFn: () => get<Paginated<ArticleListItem>>('/articles', params),
    placeholderData: (previous) => previous, // no flash of empty table between filters
    enabled,
  });

export const useArticle = (id: number | string | undefined, options?: Partial<UseQueryOptions<{ item: ArticleDetail }>>) =>
  useQuery({
    queryKey: keys.article(id ?? 'new'),
    queryFn: () => get<{ item: ArticleDetail }>(`/articles/${id}`),
    enabled: id !== undefined && id !== 'new',
    ...options,
  });

export const useReadiness = (kind: 'articles' | 'services', id: number | string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: keys.readiness(kind, id ?? ''),
    queryFn: () => get<Readiness>(`/${kind}/${id}/readiness`),
    enabled: enabled && id !== undefined && id !== 'new',
    staleTime: 0,
  });

export const useServices = (enabled = true) =>
  useQuery({ queryKey: keys.services(), queryFn: () => get<Paginated<ServiceListItem>>('/services'), enabled });

export const useService = (id: string | undefined) =>
  useQuery({
    queryKey: keys.service(id ?? 'new'),
    queryFn: () => get<{ item: ServiceDetail }>(`/services/${id}`),
    enabled: Boolean(id) && id !== 'new',
  });

export const useStories = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: keys.stories(params),
    queryFn: () => listStories(params),
    placeholderData: (previous) => previous,
  });

export const useStory = (id: string | undefined) =>
  useQuery({
    queryKey: keys.story(id ?? 'new'),
    queryFn: () => getStory(id!),
    enabled: Boolean(id) && id !== 'new',
  });

export const useCategories = () =>
  useQuery({ queryKey: keys.categories, queryFn: () => get<{ items: CategoryItem[] }>('/categories') });

export const usePages = () =>
  useQuery({ queryKey: keys.pages, queryFn: () => get<{ items: PageItem[] }>('/pages') });

export const usePage = (route: string | undefined) =>
  useQuery({
    queryKey: keys.page(route ?? ''),
    queryFn: () => get<{ item: PageItem }>(`/pages/${encodeURIComponent(route!)}`),
    enabled: Boolean(route),
  });

export const useMedia = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: keys.media(params),
    queryFn: () => get<Paginated<MediaItem>>('/media', params),
    placeholderData: (previous) => previous,
  });

export const useMediaUsage = (id: string | null) =>
  useQuery({
    queryKey: keys.mediaUsage(id ?? ''),
    queryFn: () => get<{ usedBy: Array<{ entity: string; id: string | number; label: string }> }>(`/media/${id}/usage`),
    enabled: Boolean(id),
  });

export const useRedirects = (params: Record<string, unknown> = {}) =>
  useQuery({ queryKey: keys.redirects(params), queryFn: () => get<Paginated<RedirectItem>>('/redirects', params) });

export const useLeads = (params: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: keys.leads(params),
    queryFn: () => get<Paginated<LeadItem> & { unread: number }>('/leads', params),
    placeholderData: (previous) => previous,
  });

// ── RS skaner telemetriyasi ─────────────────────────────────────────────

export const useScans = (params: ScanFilters & { page?: number; pageSize?: number }) =>
  useQuery({
    queryKey: keys.scans(params),
    queryFn: () => get<Paginated<ScanEvent>>('/scans', params),
    // A warehouse is scanning right now; a stale table is a wrong table.
    refetchInterval: 30_000,
  });

export const useScanStats = (params: ScanFilters) =>
  useQuery({
    queryKey: keys.scanStats(params),
    queryFn: () => get<ScanStats>('/scans/stats', params),
    refetchInterval: 30_000,
  });

export const useScanMap = (params: ScanFilters & { zoom: number }) =>
  useQuery({
    queryKey: keys.scanMap(params),
    queryFn: () => get<ScanMapData>('/scans/map', params),
    // The map keeps the previous frame while the next zoom level loads —
    // blanking the canvas on every pan reads as a bug.
    placeholderData: (previous) => previous,
  });

/**
 * CSV eksport. `window.open` yaramaydi — eksport admin endpoint'i va token
 * faqat xotirada, ya'ni brauzer so'roviga Authorization sarlavhasi qo'shilmaydi.
 * Shu sababli axios bilan blob sifatida olinadi.
 */
export async function downloadScansCsv(params: ScanFilters): Promise<void> {
  const res = await api.get('/scans/export.csv', { params, responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'skanlar.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const useScanOperators = () =>
  useQuery({
    queryKey: keys.scanOperators,
    queryFn: () => get<{ items: Array<{ username: string; count: number }> }>('/scans/operators'),
    staleTime: 5 * 60_000,
  });

export const useUsers = () =>
  useQuery({ queryKey: keys.users, queryFn: () => get<{ items: User[] }>('/users'), retry: false });

export const useRoles = () =>
  useQuery({
    queryKey: keys.roles,
    queryFn: () => get<{ items: RoleItem[]; pages: PageDef[] }>('/roles'),
    retry: false,
  });

export const useAudit = (params: Record<string, unknown> = {}) =>
  useQuery({ queryKey: keys.audit(params), queryFn: () => get<Paginated<AuditEntry>>('/audit-log', params), retry: false });

// ── Writes ──────────────────────────────────────────────────────────────

export interface SaveResult {
  item: ArticleDetail;
  redirects?: string[];
  revalidation?: RevalidationResult;
}

export function useSaveArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: number | 'new'; patch: Record<string, unknown> }) => {
      if (id === 'new') return (await api.post<SaveResult>('/articles', patch)).data;
      return (await api.patch<SaveResult>(`/articles/${id}`, patch)).data;
    },
    onSuccess: (data) => {
      qc.setQueryData(keys.article(data.item.id), { item: data.item });
      void qc.invalidateQueries({ queryKey: ['articles'] });
      void qc.invalidateQueries({ queryKey: keys.overview });
      void qc.invalidateQueries({ queryKey: ['readiness'] });
    },
  });
}

export function usePublishArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, publish }: { id: number; publish: boolean }) =>
      (await api.post<SaveResult>(`/articles/${id}/${publish ? 'publish' : 'unpublish'}`)).data,
    onSuccess: (data) => {
      qc.setQueryData(keys.article(data.item.id), { item: data.item });
      void qc.invalidateQueries({ queryKey: ['articles'] });
      void qc.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

export function useDeleteArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/articles/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['articles'] });
      void qc.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

export function useSaveService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string | 'new'; patch: Record<string, unknown> }) => {
      if (id === 'new') return (await api.post<{ item: ServiceDetail }>('/services', patch)).data;
      return (await api.patch<{ item: ServiceDetail }>(`/services/${id}`, patch)).data;
    },
    onSuccess: (data) => {
      qc.setQueryData(keys.service(data.item.id), { item: data.item });
      void qc.invalidateQueries({ queryKey: keys.services() });
    },
  });
}

export function usePublishService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, publish }: { id: string; publish: boolean }) =>
      (await api.post<{ item: ServiceDetail }>(`/services/${id}/${publish ? 'publish' : 'unpublish'}`)).data,
    onSuccess: (data) => {
      qc.setQueryData(keys.service(data.item.id), { item: data.item });
      void qc.invalidateQueries({ queryKey: keys.services() });
    },
  });
}

export function useDeleteService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/services/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.services() });
      void qc.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

export function useSaveStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string | 'new'; input: StoryInput & { expectedUpdatedAt?: string } }) => {
      if (id === 'new') return createStory(input);
      return updateStory(id, input);
    },
    onSuccess: (data: { item: StoryDetail }) => {
      qc.setQueryData(keys.story(data.item.id), { item: data.item });
      void qc.invalidateQueries({ queryKey: ['stories'] });
    },
  });
}

export function usePublishStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, publish }: { id: string; publish: boolean }) =>
      (publish ? publishStoryApi(id) : unpublishStory(id)),
    onSuccess: (data: { item: StoryDetail }) => {
      qc.setQueryData(keys.story(data.item.id), { item: data.item });
      void qc.invalidateQueries({ queryKey: ['stories'] });
    },
  });
}

export function useDeleteStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteStory(id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stories'] });
      void qc.invalidateQueries({ queryKey: keys.overview });
    },
  });
}

export function useRestoreStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => restoreStoryApi(id),
    onSuccess: (data: { item: StoryDetail }) => {
      qc.setQueryData(keys.story(data.item.id), { item: data.item });
      void qc.invalidateQueries({ queryKey: ['stories'] });
    },
  });
}

export function useReorderStories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => reorderStories(ids),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stories'] }),
  });
}

export function useSavePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ route, patch }: { route: string; patch: Record<string, unknown> }) =>
      (await api.patch<{ item: PageItem }>(`/pages/${encodeURIComponent(route)}`, patch)).data,
    onSuccess: (data) => {
      qc.setQueryData(keys.page(data.item.route), { item: data.item });
      void qc.invalidateQueries({ queryKey: keys.pages });
    },
  });
}

export function useUploadMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return (await api.post<{ item: MediaItem }>('/media', form)).data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['media'] }),
  });
}

export function useInvalidate() {
  const qc = useQueryClient();
  return (key: readonly unknown[]) => qc.invalidateQueries({ queryKey: key as unknown[] });
}
